/**
 * Hook useSearchQuery
 * 
 * Versão otimizada com React Query para busca de produtos
 */

import { useQuery } from '@tanstack/react-query';
import { useDebounce } from './useDebounce';
import { getProductSuggestions, searchProducts } from '../services/api';
import { handleError } from '../utils/errorHandler';

/**
 * @param {string} searchTerm - Termo de busca
 * @param {Object} options - Opções
 * @param {number} options.debounceDelay - Delay do debounce em ms (padrão: 300)
 * @param {number} options.minLength - Tamanho mínimo para buscar (padrão: 2)
 * @param {boolean} options.suggestionsOnly - Se true, busca apenas sugestões
 * @returns {Object} { results, suggestions, suggestionProducts, loading, error, search }
 */
export const useSearchQuery = (searchTerm = '', options = {}) => {
  const {
    debounceDelay = 300,
    minLength = 2,
    suggestionsOnly = false,
    enabled = true,
    staleTime = 1 * 60 * 1000, // 1 minuto (sugestões mudam frequentemente)
    ...queryOptions
  } = options;

  const debouncedSearchTerm = useDebounce(searchTerm, debounceDelay);
  const shouldSearch = debouncedSearchTerm.trim().length >= minLength;

  // Query para sugestões (autocomplete)
  const suggestionsQuery = useQuery({
    queryKey: ['productSuggestions', debouncedSearchTerm],
    queryFn: async () => {
      if (!shouldSearch) return { suggestions: [], products: [] };
      return await getProductSuggestions(debouncedSearchTerm, 5);
    },
    enabled: enabled && shouldSearch,
    staleTime,
    retry: 1,
    ...queryOptions,
  });

  // Query para resultados completos (apenas se não for apenas sugestões)
  const searchQuery = useQuery({
    queryKey: ['productSearch', debouncedSearchTerm],
    queryFn: async () => {
      if (!shouldSearch) return [];
      return await searchProducts(debouncedSearchTerm);
    },
    enabled: enabled && shouldSearch && !suggestionsOnly,
    staleTime,
    retry: 1,
    ...queryOptions,
  });

  // Tratamento de erros
  if (suggestionsQuery.error) {
    handleError(suggestionsQuery.error, {
      context: 'useSearchQuery.suggestions',
      showToast: false,
    });
  }

  if (searchQuery.error) {
    handleError(searchQuery.error, {
      context: 'useSearchQuery.search',
      showToast: false,
    });
  }

  return {
    suggestions: suggestionsQuery.data?.suggestions || [],
    suggestionProducts: suggestionsQuery.data?.products || [],
    results: searchQuery.data || [],
    loading: suggestionsQuery.isLoading || searchQuery.isLoading,
    error: suggestionsQuery.error || searchQuery.error,
    // Função para forçar busca manual
    search: async (term = debouncedSearchTerm) => {
      if (term.trim().length < minLength) return [];
      try {
        return await searchProducts(term);
      } catch (error) {
        handleError(error, {
          context: 'useSearchQuery.search',
          showToast: false,
        });
        return [];
      }
    },
  };
};

export default useSearchQuery;

